import appIcon from '../assets/app-icon.png';
import {
  Users,
  Settings,
  LayoutDashboard,
  CalendarDays,
  UserCog,
  Inbox,
  ChevronLeft,
  ChevronDown,
  ChevronRight,
  FolderKanban,
  Activity,
} from 'lucide-react';
import {
  Sidebar,
  SidebarContent,
  SidebarFooter,
  SidebarGroup,
  SidebarGroupContent,
  SidebarHeader,
  SidebarMenu,
  SidebarMenuBadge,
  SidebarMenuButton,
  SidebarMenuItem,
  SidebarRail,
  SidebarSeparator,
  useSidebar,
} from '@/components/ui/sidebar';
import { CompanySelector } from '@/components/CompanySelector';
import { Button } from '@/components/ui/button';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Collapsible, CollapsibleContent, CollapsibleTrigger } from '@/components/ui/collapsible';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { TourHelpButton } from '@/components/TourHelpButton';
import { useUser } from '@/contexts/UserContext';
import { useScheduler } from '@/contexts/SchedulerContext';
import { useMemo, useState } from 'react';

type PageName = 'overview' | 'calendar' | 'heatmap' | 'resources' | 'projects' | 'settings' | 'unallocated' | 'dependent-fields';

interface AppSidebarProps {
  currentPage: PageName;
  onNavigate: (page: PageName) => void;
  settingsTab?: string;
  onNavigateSettings?: (tab: string) => void;
}

const navItems = [
  {
    id: 'overview' as PageName,
    label: 'Dashboard',
    icon: LayoutDashboard,
    tourId: undefined,
  },
  {
    id: 'calendar' as PageName,
    label: 'Planner',
    icon: CalendarDays,
    tourId: 'navigation',
  },
  {
    id: 'heatmap' as PageName,
    label: 'Heatmap',
    icon: Activity,
    tourId: undefined,
  },
  {
    id: 'unallocated' as PageName,
    label: 'Open Demand',
    icon: Inbox,
    tourId: undefined,
  },
  {
    id: 'resources' as PageName,
    label: 'Resources',
    icon: Users,
    tourId: undefined,
  },
  {
    id: 'projects' as PageName,
    label: 'Projects',
    icon: FolderKanban,
    tourId: undefined,
  },
];

function SidebarRoleLabel() {
  const { state } = useSidebar();
  const { user } = useUser();
  const isCollapsed = state === 'collapsed';

  if (isCollapsed) {
    return (
      <TooltipProvider delayDuration={100}>
        <Tooltip>
          <TooltipTrigger asChild>
            <div className="flex justify-center py-1">
              <UserCog className="h-4 w-4 text-teal-600" />
            </div>
          </TooltipTrigger>
          <TooltipContent side="right" align="center">
            <p className="text-xs font-medium">{user.workspaceRole}</p>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  }

  return (
    <div className="flex items-center gap-1.5 px-2 py-1">
      <UserCog className="h-4 w-4 text-teal-600 shrink-0" />
      <span className="text-xs font-medium text-sidebar-foreground truncate">{user.workspaceRole}</span>
    </div>
  );
}

export function AppSidebar({ currentPage, onNavigate, settingsTab, onNavigateSettings }: AppSidebarProps) {
  const { state, toggleSidebar } = useSidebar();
  const { user } = useUser();
  const { bookings } = useScheduler();
  const isCollapsed = state === 'collapsed';
  const [settingsOpen, setSettingsOpen] = useState(currentPage === 'settings');
  const isAdministrator = user.workspaceRole === 'Administrator';

  const settingsMenuItems = useMemo(() => {
    const items: { tab: string; label: string }[] = [
      { tab: 'settings', label: 'General' },
    ];
    if (isAdministrator) {
      items.push({ tab: 'admin', label: 'Administrator' });
      items.push({ tab: 'integrations', label: 'Integration' });
    }
    return items;
  }, [isAdministrator]);

  const unallocatedStats = useMemo(() => {
    const unallocated = bookings.filter(b => b.resourceId === 'unallocated');
    const now = new Date();
    const in3Days = new Date(now.getTime() + 3 * 24 * 60 * 60 * 1000);
    const in7Days = new Date(now.getTime() + 7 * 24 * 60 * 60 * 1000);
    const critical = unallocated.filter(b => new Date(b.startTime) <= in3Days).length;
    const soon = unallocated.filter(b => {
      const start = new Date(b.startTime);
      return start > in3Days && start <= in7Days;
    }).length;
    const upcoming = unallocated.filter(b => new Date(b.startTime) > in7Days).length;
    return { total: unallocated.length, critical, soon, upcoming };
  }, [bookings]);

  const badgeLabel = unallocatedStats.total > 20 ? '20+' : String(unallocatedStats.total);

  const badgeColour =
    unallocatedStats.critical > 0
      ? 'bg-destructive text-destructive-foreground'
      : unallocatedStats.soon > 0
      ? 'bg-yellow-500 text-white'
      : 'bg-primary text-primary-foreground';

  return (
    <Sidebar collapsible="icon" variant="sidebar">

      {/* ── Header: app identity + collapse trigger ── */}
      <SidebarHeader className="px-[21px] shrink-0 h-[59px] flex !flex-row items-center border-b overflow-visible">
        <div className="flex items-center w-full">
          {isCollapsed ? (
            /* Collapsed: only expand chevron */
            <TooltipProvider delayDuration={100}>
              <Tooltip>
                <TooltipTrigger asChild>
                  <Button
                    variant="ghost"
                    size="icon"
                    className="mx-auto h-7 w-7 text-sidebar-foreground hover:bg-sidebar-accent/40"
                    onClick={toggleSidebar}
                    aria-label="Expand sidebar"
                  >
                    <ChevronRight className="h-5 w-5 stroke-[2.5]" />
                  </Button>
                </TooltipTrigger>
                <TooltipContent side="right">
                  <p className="text-xs">Expand sidebar</p>
                </TooltipContent>
              </Tooltip>
            </TooltipProvider>
          ) : (
            /* Expanded: name on left, collapse arrow on far right */
            <>
              <div className="flex items-center gap-2 flex-1">
                <img src={appIcon} alt="Access Planner" className="h-7 w-7 rounded-md object-cover shrink-0" />
                <span className="text-base font-bold text-sidebar-foreground whitespace-nowrap">
                  Access Planner
                </span>
              </div>
              <TooltipProvider delayDuration={100}>
                <Tooltip>
                  <TooltipTrigger asChild>
                    <Button
                      variant="ghost"
                      size="icon"
                      className="shrink-0 h-7 w-7 text-sidebar-foreground/60 hover:text-sidebar-foreground hover:bg-sidebar-accent/40"
                      onClick={toggleSidebar}
                    >
                      <ChevronLeft className="h-5 w-5 stroke-[2.5]" />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent side="right">
                    <p className="text-xs">Collapse sidebar</p>
                  </TooltipContent>
                </Tooltip>
              </TooltipProvider>
            </>
          )}
        </div>
      </SidebarHeader>

      {/* ── Company Selector ── */}
      <SidebarContent className="flex-none overflow-hidden group-data-[collapsible=icon]:hidden">
        <SidebarGroup className="px-3 py-3">
          <SidebarGroupContent>
            <CompanySelector sidebarMode />
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* ── Main navigation ── */}
      <SidebarContent className="pt-2">
        <SidebarGroup className="px-[17px] pt-0">
          <SidebarGroupContent>
            <SidebarMenu data-tour="navigation">
              {navItems.map((item) => {
                const Icon = item.icon;
                const isActive = currentPage === item.id;
                const isUnallocated = item.id === 'unallocated';

                /* Open Demand — rich tooltip with demand breakdown when collapsed */
                if (isUnallocated && isCollapsed) {
                  return (
                    <SidebarMenuItem key={item.id}>
                      <TooltipProvider delayDuration={100}>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <SidebarMenuButton
                              isActive={isActive}
                              onClick={() => onNavigate(item.id)}
                              className="relative justify-start group-data-[collapsible=icon]:justify-center [&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 data-[active=true]:bg-teal-700 data-[active=true]:text-white data-[active=true]:hover:bg-teal-700"
                            >
                              <Icon />
                              <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>
                              {unallocatedStats.total > 0 && (
                                <span
                                  className={`absolute top-0.5 right-0.5 h-2 w-2 rounded-full border border-sidebar ${
                                    unallocatedStats.critical > 0
                                      ? 'bg-destructive'
                                      : unallocatedStats.soon > 0
                                      ? 'bg-yellow-500'
                                      : 'bg-primary'
                                  }`}
                                />
                              )}
                            </SidebarMenuButton>
                          </TooltipTrigger>
                          <TooltipContent side="right" className="text-xs space-y-1 p-3 min-w-[180px]">
                            <p className="font-semibold mb-1.5">
                              {unallocatedStats.total > 0
                                ? `${unallocatedStats.total} unresourced booking${unallocatedStats.total !== 1 ? 's' : ''}`
                                : 'Open Demand'}
                            </p>
                            {unallocatedStats.critical > 0 && (
                              <p className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-destructive inline-block shrink-0" />
                                <span className="text-destructive font-medium">{unallocatedStats.critical} starting within 3 days</span>
                              </p>
                            )}
                            {unallocatedStats.soon > 0 && (
                              <p className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-yellow-500 inline-block shrink-0" />
                                <span className="text-yellow-600 dark:text-yellow-400 font-medium">{unallocatedStats.soon} starting within 7 days</span>
                              </p>
                            )}
                            {unallocatedStats.upcoming > 0 && (
                              <p className="flex items-center gap-1.5">
                                <span className="h-2 w-2 rounded-full bg-muted-foreground inline-block shrink-0" />
                                <span className="text-muted-foreground">{unallocatedStats.upcoming} upcoming</span>
                              </p>
                            )}
                            {unallocatedStats.total === 0 && (
                              <p className="text-muted-foreground">No unresourced bookings</p>
                            )}
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    </SidebarMenuItem>
                  );
                }

                return (
                  <SidebarMenuItem key={item.id}>
                    <SidebarMenuButton
                      isActive={isActive}
                      tooltip={item.label}
                      onClick={() => onNavigate(item.id)}
                      data-tour={item.tourId}
                      className="relative justify-start group-data-[collapsible=icon]:justify-center [&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 data-[active=true]:bg-teal-700 data-[active=true]:text-white data-[active=true]:hover:bg-teal-700"
                    >
                      <Icon />
                      <span className="group-data-[collapsible=icon]:hidden">{item.label}</span>

                      {isUnallocated && !isCollapsed && unallocatedStats.total > 0 && (
                        <SidebarMenuBadge
                          className={`${
                            isActive
                              ? 'bg-background/20 text-inherit'
                              : badgeColour
                          } text-[10px] font-bold h-4 min-w-[18px] px-1`}
                        >
                          {badgeLabel}
                        </SidebarMenuBadge>
                      )}
                    </SidebarMenuButton>
                  </SidebarMenuItem>
                );
              })}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>

      {/* ── Footer: role selector → Settings (collapsible) → Help & Tour ── */}
      <SidebarFooter className="gap-1 pb-3 pt-2">
        <SidebarRoleLabel />

        {/* Settings — dropdown flyout when collapsed, collapsible when expanded */}
        <SidebarMenu>
          {isCollapsed ? (
            /* ── Collapsed: flyout dropdown ── */
            <SidebarMenuItem>
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <SidebarMenuButton
                    isActive={currentPage === 'settings'}
                    tooltip="Settings"
                    data-tour="settings"
                    className="justify-start [&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 data-[active=true]:bg-teal-700 data-[active=true]:text-white data-[active=true]:hover:bg-teal-700"
                  >
                    <Settings />
                    <span>Settings</span>
                  </SidebarMenuButton>
                </DropdownMenuTrigger>
                <DropdownMenuContent side="right" align="end" className="w-52 ml-1">
                  {settingsMenuItems.map(({ tab, label }) => (
                    <DropdownMenuItem
                      key={tab}
                      onClick={() => onNavigateSettings?.(tab)}
                      className={`cursor-pointer text-sm font-normal ${
                        currentPage === 'settings' && settingsTab === tab
                          ? 'bg-sidebar-accent text-sidebar-accent-foreground font-medium'
                          : ''
                      }`}
                    >
                      {label}
                    </DropdownMenuItem>
                  ))}
                </DropdownMenuContent>
              </DropdownMenu>
            </SidebarMenuItem>
          ) : (
            /* ── Expanded: collapsible inline ── */
            <Collapsible
              open={settingsOpen}
              onOpenChange={(open) => {
                setSettingsOpen(open);
              }}
            >
              <SidebarMenuItem>
                <CollapsibleTrigger asChild>
                  <SidebarMenuButton
                    isActive={false}
                    data-tour="settings"
                    className="justify-start [&>svg]:h-4 [&>svg]:w-4 text-sm font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 data-[active=true]:bg-teal-700 data-[active=true]:text-white data-[active=true]:hover:bg-teal-700"
                  >
                    <Settings />
                    <span>Settings</span>
                    {settingsOpen
                      ? <ChevronDown className="ml-auto h-4 w-4 shrink-0 text-teal-600" />
                      : <ChevronRight className="ml-auto h-4 w-4 shrink-0 text-teal-600" />
                    }
                  </SidebarMenuButton>
                </CollapsibleTrigger>

                <CollapsibleContent>
                  <div className="ml-[1.35rem] pl-3 border-l border-sidebar-border mt-1 space-y-0.5">
                    {settingsMenuItems.map(({ tab, label }) => (
                      <SidebarMenuItem key={tab}>
                        <SidebarMenuButton
                          isActive={currentPage === 'settings' && settingsTab === tab}
                          onClick={() => onNavigateSettings?.(tab)}
                          className="justify-start text-sm font-medium text-sidebar-foreground/75 hover:text-sidebar-foreground hover:bg-sidebar-accent/50 data-[active=true]:bg-teal-700 data-[active=true]:text-white data-[active=true]:hover:bg-teal-700"
                        >
                          <span>{label}</span>
                        </SidebarMenuButton>
                      </SidebarMenuItem>
                    ))}
                  </div>
                </CollapsibleContent>
              </SidebarMenuItem>
            </Collapsible>
          )}
        </SidebarMenu>

        <SidebarMenu>
          <SidebarMenuItem>
            <TourHelpButton />
          </SidebarMenuItem>
        </SidebarMenu>
      </SidebarFooter>

      <SidebarRail />
    </Sidebar>
  );
}
